import cn from 'classnames';
import type { FC } from 'react';

import Link from '@/components/_rebrand/Link';

import Icon from '../Icon';
import { LogoTile, type LogoTileProps } from './LogoTile';
import { QuoteTile, type QuoteTileProps } from './QuoteTile';
import { StatTile, type StatTileProps } from './StatTile';

const TILE_LAYOUT: Record<SocialProofTileProps['type'], { container: string; iconPosition: string }> = {
  logo: {
    container: 'w-[160px] h-[94px] items-center justify-center gap-3xs px-lg py-2xs',
    iconPosition: 'top-1 right-1',
  },
  stat: {
    container: 'w-[215px] h-[200px] items-start justify-between gap-xs px-md py-xs',
    iconPosition: 'top-spacing-12 right-spacing-12',
  },
  quote: {
    container: 'w-[295px] h-[200px] items-start gap-[6px] pb-s pl-s pr-xs pt-xs',
    iconPosition: 'top-spacing-12 right-spacing-12',
  },
};

type SocialProofTileCommonProps = {
  href?: string;
  className?: string;
};

type TileVariantProps =
  | ({ type: 'logo' } & LogoTileProps)
  | ({ type: 'stat' } & StatTileProps)
  | ({ type: 'quote' } & QuoteTileProps);

export type SocialProofTileProps = SocialProofTileCommonProps & TileVariantProps;

const TILE_COMPONENTS: {
  [K in TileVariantProps['type']]: FC<Extract<TileVariantProps, { type: K }>>
} = {
  logo: ({ logo }) => <LogoTile logo={logo} />,
  stat: ({ logo, metric, description }) => <StatTile logo={logo} metric={metric} description={description} />,
  quote: ({ logo, quote, authorName, authorTitle }) => <QuoteTile logo={logo} quote={quote} authorName={authorName} authorTitle={authorTitle} />,
};

const SocialProofTileContent: FC<SocialProofTileProps> = (props) => {
  const { type, href, className } = props;
  const tileLayout = TILE_LAYOUT[type];
  const TileComponent = TILE_COMPONENTS[props.type] as FC<TileVariantProps>;

  return (
    <div
      className={cn(
        'surface-l1 relative flex flex-col rounded-8',
        href && 'transition-[box-shadow,transform] duration-600 ease-in-out group-hover:shadow-social-proof-tile sm:group-hover:-translate-y-[12px]',
        tileLayout.container,
        className,
      )}
    >
      {href && (
        <div className={cn('flex absolute z-10', tileLayout.iconPosition)}>
          <span className={cn(
            'surface-l2 rounded-full p-[6px]',
            'transition-colors duration-600 ease-in-out',
            'group-hover:surface-l1-hover'
          )}>
            <Icon name="arrow-narrow-up-right" size={12} className={cn(
              'icon-action-inactive',
              'group-hover:icon-action-active'
            )} />
          </span>
        </div>
      )}
      <TileComponent {...props} />
    </div>
  );
};

export const SocialProofTile: FC<SocialProofTileProps> = (props) => {
  const { href } = props;

  if (!href) return <SocialProofTileContent {...props} />;

  return (
    <Link
      href={href}
      className="group"
      tabIndex={-1} // To prevent the link from being focusable when the tile is rendered inside a slider
    >
      <SocialProofTileContent {...props} />
    </Link>
  );
};
